import { Trash2, User, Mail, Phone } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { AvailabilitySwitch } from "../attorneys/AvailabilitySwitch"

const AVAILABLE_LABEL = "Available"

interface Attorney {
  id: string
  name: string
  email: string
  available?: boolean
  phone: string
}

interface AttorneyCardProps {
  attorney: Attorney
  onSelect: (attorneyId: string) => void
  onDelete: (attorneyId: string) => void
  onAvailabilityChange: (attorneyId: string, available: boolean) => void
}

export default function AttorneyCard({ attorney, onSelect, onDelete, onAvailabilityChange }: Readonly<AttorneyCardProps>) {
  const renderToggle = () => (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500">{AVAILABLE_LABEL}</span>
      <AvailabilitySwitch
        checked={attorney.available === true}
        onChange={(available) => onAvailabilityChange(attorney.id, available)}
      />
    </div>
  )

  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-2">
        <div className="hidden md:flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-32 lg:w-40">
              <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <h3
                className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                onClick={() => onSelect(attorney.id)}
              >
                {attorney.name}
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 w-48 lg:w-56">
              <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <a href={`mailto:${attorney.email}`} className="text-sm text-slate-700 hover:text-slate-900 transition-colors truncate">
                {attorney.email}
              </a>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 w-36 lg:w-44">
              <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-700 truncate">+{attorney.phone}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {renderToggle()}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
              onClick={() => onDelete(attorney.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="md:hidden">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <h3
                  className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                  onClick={() => onSelect(attorney.id)}
                >
                  {attorney.name}
                </h3>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <a href={`mailto:${attorney.email}`} className="text-sm text-slate-700 hover:text-slate-900 transition-colors">
                  {attorney.email}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">+{attorney.phone}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {renderToggle()}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                onClick={() => onDelete(attorney.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
